/* ============================================
   SARĀCA — Product Page JavaScript
   Proper select-based variant selection
   ============================================ */

(function () {
  'use strict';

  const BH = window.BrewHouse || {};

  /* ─────────────────────────────────────────
     Helpers for purchase options
     ───────────────────────────────────────── */
  const escapeHtml = (text) => String(text == null ? '' : text)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  /* Formats cents with the shop's money format, including thousands separators */
  function formatMoney(cents, format) {
    const value = Number(cents) / 100;
    const group = (n, decimals, thousands, decimal) => {
      const [whole, fraction] = n.toFixed(decimals).split('.');
      const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? withThousands + decimal + fraction : withThousands;
    };
    return (format || '₹{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
        default: return group(value, 2, ',', '.');
      }
    });
  }

  const ordinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd']; const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const ICONS = {
    truck: '<path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>',
    cancel: '<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6"/>',
    shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/>',
    gift: '<path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>',
    calendar: '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>',
    coffee: '<path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><path d="M6 1v3M10 1v3M14 1v3"/>',
  };
  const icon = (name) => `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[name] || ICONS.truck}</svg>`;

  /* Sample plans for the theme editor preview only (never used for real orders) */
  const SAMPLE_PLANS = [
    { id: 'sample-8', label: '8 Days', adjustments: [{ order_count: 1, position: 1, value_type: 'percentage', value: 0 }, { order_count: null, position: 2, value_type: 'percentage', value: 15 }] },
    { id: 'sample-16', label: '16 Days', adjustments: [{ order_count: 1, position: 1, value_type: 'percentage', value: 0 }, { order_count: null, position: 2, value_type: 'percentage', value: 12 }] },
    { id: 'sample-24', label: '24 Days', adjustments: [{ order_count: 1, position: 1, value_type: 'percentage', value: 0 }, { order_count: null, position: 2, value_type: 'percentage', value: 10 }] },
  ];

  /* ─────────────────────────────────────────
     Purchase options: One-time purchase vs Subscribe & save
     Drawn from the product's selling plans (subscription app),
     or from sample plans inside the theme editor.
     ───────────────────────────────────────── */
  class PurchaseOptions {
    constructor(el, page) {
      this.el = el;
      this.page = page;
      this.sample = el.hasAttribute('data-sample');
      this.root = el.querySelector('[data-purchase-options-root]');
      this.input = el.querySelector('[data-selling-plan-input]');
      this.dynamicCheckout = page.form.querySelector('[data-dynamic-checkout]');
      this.uid = Math.random().toString(36).slice(2, 8);
      try {
        this.config = JSON.parse(el.querySelector('[data-purchase-options-config]').textContent);
      } catch (e) {
        this.config = {};
      }
      this.addLabel = page.addToCartBtn ? page.addToCartBtn.textContent.trim() : 'Add to cart';

      const product = page.productData || {};
      this.plans = this.sample ? SAMPLE_PLANS : (product.selling_plan_groups || []).flatMap((group) =>
        (group.selling_plans || []).map((plan) => ({
          id: plan.id,
          label: (plan.options && plan.options[0] && plan.options[0].value) || plan.name,
          adjustments: (plan.price_adjustments || []).slice().sort((a, b) => a.position - b.position),
        }))
      );
      if (!this.plans.length) { el.hidden = true; return; }

      const currentId = Number(page.variantIdInput && page.variantIdInput.value);
      this.variant = (product.variants || []).find((v) => v.id === currentId) || (product.variants || [])[0];
      this.mode = this.config.requiresPlan || this.config.defaultMode !== 'one_time' ? 'subscribe' : 'one_time';
      this.planId = this.plans[0].id;

      this.render();
      this.root.addEventListener('change', (e) => {
        if (e.target.name === `po-mode-${this.uid}`) this.setMode(e.target.value);
        if (e.target.name === `po-plan-${this.uid}`) this.setPlan(e.target.value);
      });
      this.sync();
    }

    /* Prices for a plan on the current variant: first delivery, later deliveries */
    pricesFor(plan) {
      const variant = this.variant;
      if (!variant) return null;
      const apply = (price, adj) => {
        if (!adj) return price;
        if (adj.value_type === 'percentage') return Math.round(price * (100 - adj.value) / 100);
        if (adj.value_type === 'fixed_amount') return Math.max(0, price - adj.value);
        if (adj.value_type === 'price') return adj.value;
        return price;
      };
      const adj = plan.adjustments;
      const firstCount = adj.length > 1 && adj[0].order_count ? adj[0].order_count : null;
      if (this.sample) {
        return { first: apply(variant.price, adj[0]), later: apply(variant.price, adj[adj.length - 1]), firstCount, base: variant.price };
      }
      const allocation = (variant.selling_plan_allocations || []).find((a) => String(a.selling_plan_id) === String(plan.id));
      if (!allocation) return null;
      const priced = (allocation.price_adjustments || []).slice().sort((a, b) => a.position - b.position);
      return {
        first: priced.length ? priced[0].price : allocation.price,
        later: priced.length ? priced[priced.length - 1].price : allocation.price,
        firstCount,
        base: allocation.compare_at_price || variant.price,
      };
    }

    percentOff(price, base) {
      return base > 0 ? Math.round((1 - price / base) * 100) : 0;
    }

    money(cents) {
      return formatMoney(cents, this.config.moneyFormat);
    }

    render() {
      const c = this.config;
      const u = this.uid;
      const benefits = (c.benefits || []).filter((b) => b && b.text);
      this.root.innerHTML = `
        <div class="po__modes" role="radiogroup" aria-label="Purchase options">
          ${c.requiresPlan ? '' : `
          <label class="po__mode" data-po-mode="one_time">
            <input type="radio" name="po-mode-${u}" value="one_time">
            <span class="po__radio" aria-hidden="true"></span>
            <span class="po__mode-text"><span class="po__mode-title">${escapeHtml(c.oneTimeTitle || 'One-time purchase')}</span><span class="po__mode-sub">${escapeHtml(c.oneTimeText || '')}</span></span>
          </label>`}
          <label class="po__mode" data-po-mode="subscribe">
            <input type="radio" name="po-mode-${u}" value="subscribe">
            <span class="po__radio" aria-hidden="true"></span>
            <span class="po__mode-text"><span class="po__mode-title">${escapeHtml(c.subscribeTitle || 'Subscribe & save')}</span><span class="po__mode-sub">${escapeHtml(c.subscribeText || '')}</span></span>
          </label>
        </div>
        <div class="po__subscribe" data-po-subscribe>
          <div class="po__panel">
            <div class="po__panel-head">
              <span class="po__badge" data-po-badge></span>
              <span class="po__panel-title">${escapeHtml(c.subscribeTitle || 'Subscribe & save')}</span>
            </div>
            ${c.panelText ? `<p class="po__panel-text">${escapeHtml(c.panelText)}</p>` : ''}
            <div class="po__prices" data-po-prices></div>
            <div class="po__freq-label">${escapeHtml(c.frequencyLabel || 'Deliver every')}</div>
            <div class="po__freqs" role="radiogroup" aria-label="${escapeHtml(c.frequencyLabel || 'Deliver every')}" data-po-freqs></div>
            ${benefits.length ? `<ul class="po__benefits">${benefits.map((b) => `<li>${icon(b.icon)}<span>${escapeHtml(b.text)}</span></li>`).join('')}</ul>` : ''}
            ${this.sample ? '<p class="po__sample">Preview with sample plans. Customers only see subscription options on products that have a subscription plan.</p>' : ''}
          </div>
          ${c.note ? `<p class="po__note">${icon('shield')}<span>${escapeHtml(c.note)}</span></p>` : ''}
        </div>`;
      this.modeInputs = Array.from(this.root.querySelectorAll(`input[name="po-mode-${u}"]`));
      this.subscribeEl = this.root.querySelector('[data-po-subscribe]');
      this.pricesEl = this.root.querySelector('[data-po-prices]');
      this.freqsEl = this.root.querySelector('[data-po-freqs]');
      this.badgeEl = this.root.querySelector('[data-po-badge]');
    }

    renderFrequencies() {
      const c = this.config;
      const u = this.uid;
      const popular = Number(c.popularIndex) || 0;
      this.freqsEl.innerHTML = this.plans.map((plan, i) => {
        const prices = this.pricesFor(plan);
        const off = prices ? this.percentOff(prices.later, prices.base) : 0;
        const tag = popular === i + 1 && c.popularLabel ? c.popularLabel : (off > 0 ? `${off}% off` : '');
        const checked = String(plan.id) === String(this.planId);
        return `
          <label class="po__freq${checked ? ' is-selected' : ''}${prices ? '' : ' is-disabled'}">
            <input type="radio" name="po-plan-${u}" value="${escapeHtml(plan.id)}"${checked ? ' checked' : ''}${prices ? '' : ' disabled'}>
            <span class="po__freq-title">${escapeHtml(plan.label)}</span>
            ${tag ? `<span class="po__freq-tag">${escapeHtml(tag)}</span>` : ''}
          </label>`;
      }).join('');
    }

    renderPrices() {
      const c = this.config;
      const plan = this.plans.find((p) => String(p.id) === String(this.planId));
      const prices = plan && this.pricesFor(plan);
      if (!prices) { this.pricesEl.innerHTML = ''; this.badgeEl.hidden = true; return; }
      const offLater = this.percentOff(prices.later, prices.base);
      const offFirst = this.percentOff(prices.first, prices.base);
      this.badgeEl.hidden = offLater <= 0;
      this.badgeEl.textContent = `${offLater}% off`;

      if (prices.firstCount && prices.first !== prices.later) {
        const n = prices.firstCount;
        this.pricesEl.innerHTML = `
          <div class="po__price-row">
            <div><div class="po__price-title">${escapeHtml(n === 1 ? (c.firstLabel || 'Your first order today') : `Your first ${n} orders`)}</div>
              <div class="po__price-sub">${offFirst > 0 ? `First ${n === 1 ? 'delivery' : n + ' deliveries'}` : `First ${n === 1 ? 'delivery' : n + ' deliveries'} – standard price`}</div></div>
            <div class="po__price-val"><div class="po__price">${this.money(prices.first)}</div><div class="po__price-sub">${offFirst > 0 ? `${offFirst}% saved` : 'full price'}</div></div>
          </div>
          <div class="po__price-divider" aria-hidden="true"><span>↓</span></div>
          <div class="po__price-row po__price-row--save">
            <div><div class="po__price-title">${escapeHtml(c.afterLabel || 'Every delivery after that')}</div>
              <div class="po__price-sub">From your ${ordinal(n + 1)} order onwards – automatically</div></div>
            <div class="po__price-val"><div class="po__price">${this.money(prices.later)}</div><div class="po__price-sub po__save">${offLater}% saved</div></div>
          </div>`;
      } else {
        this.pricesEl.innerHTML = `
          <div class="po__price-row po__price-row--save">
            <div><div class="po__price-title">Price per delivery</div><div class="po__price-sub">Delivered automatically</div></div>
            <div class="po__price-val"><div class="po__price">${this.money(prices.later)}${offLater > 0 ? ` <s class="po__compare">${this.money(prices.base)}</s>` : ''}</div>
              ${offLater > 0 ? `<div class="po__price-sub po__save">${offLater}% saved</div>` : ''}</div>
          </div>`;
      }
    }

    /* Called by ProductPage whenever the variant changes */
    onVariant(variant) {
      if (!this.plans || !this.plans.length) return;
      this.variant = variant;
      const current = this.plans.find((p) => String(p.id) === String(this.planId));
      if (!current || !this.pricesFor(current)) {
        const firstAvailable = this.plans.find((p) => this.pricesFor(p));
        if (firstAvailable) this.planId = firstAvailable.id;
      }
      this.sync();
    }

    setMode(mode) { this.mode = mode; this.sync(); }

    setPlan(id) { this.planId = id; this.sync(); }

    /* The selected plan id to send with Add to cart, or null for one-time */
    currentPlanId() {
      if (this.sample || this.mode !== 'subscribe' || !this.input || this.input.disabled) return null;
      return this.input.value ? Number(this.input.value) : null;
    }

    sync() {
      const anyAvailable = this.plans.some((p) => this.pricesFor(p));
      if (!anyAvailable && !this.config.requiresPlan) this.mode = 'one_time';
      const subscribing = this.mode === 'subscribe';

      this.modeInputs.forEach((input) => {
        input.checked = input.value === this.mode;
        input.closest('.po__mode').classList.toggle('is-selected', input.checked);
        if (input.value === 'subscribe') {
          input.disabled = !anyAvailable;
          input.closest('.po__mode').classList.toggle('is-disabled', !anyAvailable);
        }
      });
      this.subscribeEl.hidden = !subscribing;
      this.renderFrequencies();
      this.renderPrices();

      if (this.input) {
        const usable = subscribing && !this.sample && anyAvailable;
        this.input.disabled = !usable;
        this.input.value = usable ? String(this.planId) : '';
      }
      if (this.dynamicCheckout) this.dynamicCheckout.hidden = subscribing;
      this.syncButton();
    }

    syncButton() {
      const btn = this.page.addToCartBtn;
      if (!btn || btn.disabled) return;
      btn.textContent = this.mode === 'subscribe' ? (this.config.buttonLabel || 'Subscribe now') : this.addLabel;
    }
  }

  class ProductPage {
    constructor() {
      this.form = document.querySelector('[data-product-form]');
      if (!this.form) return;

      this.variantIdInput    = this.form.querySelector('[name="id"]');
      this.addToCartBtn      = this.form.querySelector('[data-add-to-cart]');
      this.priceDisplay      = document.querySelector('[data-product-price]');
      this.comparePriceDisplay = document.querySelector('[data-product-compare-price]');
      this.buyNowBtn         = document.querySelector('[data-buy-now]');

      /* Load full product JSON for variant matching */
      const jsonEl = document.querySelector('[data-product-json]');
      this.productData = jsonEl ? JSON.parse(jsonEl.textContent || '{}') : {};

      /* Subscription options (only present when the product has plans, or in the editor preview) */
      const purchaseOptionsEl = this.form.querySelector('[data-purchase-options]');
      this.purchaseOptions = purchaseOptionsEl ? new PurchaseOptions(purchaseOptionsEl, this) : null;

      /* Thumbnail/page switching is handled by the inline script in sections/main-product.liquid */
      this.initVariantSelects();
      this.initQuantity();
      this.initAddToCart();
    }

    /* ─────────────────────────────────────────
       Variant Selects — core logic
       ───────────────────────────────────────── */
    initVariantSelects() {
      this.selects = Array.from(
        this.form.querySelectorAll('[data-variant-select]')
      );
      if (!this.selects.length) return;

      this.selects.forEach((sel) =>
        sel.addEventListener('change', () => this.onVariantChange())
      );

      /* Trigger once on load to set the correct initial state */
      this.onVariantChange();
    }

    /* Collect currently selected option values → find matching variant */
    onVariantChange() {
      const selectedValues = this.selects.map((s) => s.value);
      const variant = this.findVariant(selectedValues);
      this.applyVariant(variant);
    }

    findVariant(selectedValues) {
      if (!this.productData.variants) return null;

      return this.productData.variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3].filter(Boolean);
        return selectedValues.every((val, i) => opts[i] === val);
      }) || null;
    }

    applyVariant(variant) {
      if (!variant) return;

      /* Update hidden variant ID */
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
      }

      /* Update price */
      if (this.priceDisplay && BH.formatMoney) {
        this.priceDisplay.textContent = BH.formatMoney(variant.price);
      } else if (this.priceDisplay) {
        /* Fallback: simple currency formatting */
        const amount = (variant.price / 100).toFixed(2);
        this.priceDisplay.textContent = `₹${amount}`;
      }

      /* Update compare-at price */
      if (this.comparePriceDisplay) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          if (BH.formatMoney) {
            this.comparePriceDisplay.textContent = BH.formatMoney(variant.compare_at_price);
          } else {
            const amount = (variant.compare_at_price / 100).toFixed(2);
            this.comparePriceDisplay.textContent = `₹${amount}`;
          }
          this.comparePriceDisplay.style.display = '';
        } else {
          this.comparePriceDisplay.style.display = 'none';
        }
      }

      /* Update Buy Now link */
      if (this.buyNowBtn) {
        const qty = this.form.querySelector('[name="quantity"]')?.value || 1;
        this.buyNowBtn.href = `/checkout?variant=${variant.id}&quantity=${qty}`;
      }

      /* Update Add to Cart availability */
      if (this.addToCartBtn) {
        if (!variant.available) {
          this.addToCartBtn.disabled = true;
          this.addToCartBtn.textContent = 'Sold Out';
        } else {
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.textContent = 'Add To Cart';
        }
      }

      /* Subscription prices follow the selected variant */
      if (this.purchaseOptions) this.purchaseOptions.onVariant(variant);
    }

    /* ─────────────────────────────────────────
       Quantity Stepper
       ───────────────────────────────────────── */
    initQuantity() {
      const qtyInput = this.form.querySelector('.product-quantity__input');
      const qtyMinus = this.form.querySelector('[data-qty-minus]');
      const qtyPlus  = this.form.querySelector('[data-qty-plus]');

      qtyMinus?.addEventListener('click', () => {
        const val = parseInt(qtyInput.value, 10);
        if (val > 1) qtyInput.value = val - 1;
      });

      qtyPlus?.addEventListener('click', () => {
        qtyInput.value = parseInt(qtyInput.value, 10) + 1;
      });
    }

    /* ─────────────────────────────────────────
       Add to Cart
       ───────────────────────────────────────── */
    initAddToCart() {
      this.form?.addEventListener('submit', async (e) => {
        e.preventDefault();

        const btn = this.addToCartBtn;
        if (!btn || btn.disabled) return;

        const originalText = btn.textContent;
        btn.classList.add('btn--loading');
        btn.disabled = true;

        const formData = new FormData(this.form);
        const id       = parseInt(formData.get('id'), 10);
        const quantity = parseInt(formData.get('quantity') || '1', 10);
        const item     = { id, quantity };

        /* Subscription: send the chosen selling plan */
        const sellingPlan = this.purchaseOptions ? this.purchaseOptions.currentPlanId() : null;
        if (sellingPlan) item.selling_plan = sellingPlan;

        try {
          await BH.Cart.add(item);
          BH.CartDrawer.open();
        } catch (err) {
          console.error('[Sarāca] Add to cart error:', err);
          btn.textContent = 'Error — Try Again';
          setTimeout(() => { btn.textContent = originalText; }, 2500);
        } finally {
          btn.classList.remove('btn--loading');
          btn.disabled = false;
        }
      });
    }
  }

  /* Exposed for testing and for other scripts */
  window.SaracaPurchaseOptions = { PurchaseOptions, formatMoney };

  document.addEventListener('DOMContentLoaded', () => {
    new ProductPage();
  });
})();
